import { useCallback, useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MediaPicker } from "@/components/admin/MediaPicker";

const inputCls = "bg-white border-zinc-300 text-zinc-800 focus-visible:ring-[#FF6B00] rounded-sm";

const F = ({ label, value, onChange, testid, textarea = false }) => (
  <div>
    <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</Label>
    {textarea ? (
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} min-h-[80px]`} data-testid={testid} />
    ) : (
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
    )}
  </div>
);

const SeoFields = ({ pkey, pages, setPage }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-zinc-200 pt-4">
    <F label="SEO Title (optional)" value={pages[pkey].seo_title} onChange={(v) => setPage(pkey, "seo_title", v)} testid={`${pkey}-seo-title`} />
    <F label="SEO Description (optional)" value={pages[pkey].seo_description} onChange={(v) => setPage(pkey, "seo_description", v)} testid={`${pkey}-seo-desc`} />
  </div>
);

const SectionsEditor = ({ sections, onChange }) => {
  const set = (i, k, v) => onChange(sections.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-4">
      {sections.map((s, i) => (
        <div key={s.id || i} className="border border-zinc-200 rounded-sm p-4 space-y-3" data-testid={`page-section-${i}`}>
          <div className="flex justify-between">
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move section up"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === sections.length - 1} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move section down"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            <button type="button" onClick={() => onChange(sections.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-600" aria-label="Remove section"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          <F label="Heading" value={s.heading} onChange={(v) => set(i, "heading", v)} testid={`page-section-heading-${i}`} />
          <F label="Text" value={s.text} onChange={(v) => set(i, "text", v)} textarea testid={`page-section-text-${i}`} />
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Image (optional)</Label>
            <MediaPicker value={s.image} onChange={(v) => set(i, "image", v)} testid={`page-section-image-${i}`} />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...sections, { id: `s-${Date.now()}`, heading: "", text: "", image: "" }])} className="border-zinc-300 text-zinc-600 rounded-sm text-xs uppercase font-bold" data-testid="page-section-add">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add Section
      </Button>
    </div>
  );
};

const ListPairEditor = ({ items, onChange, fields, testidPrefix }) => {
  const set = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`${testidPrefix}-row-${i}`}>
          {fields.map(([k, ph]) => (
            <Input key={k} placeholder={ph} value={it[k] || ""} onChange={(e) => set(i, k, e.target.value)} className={`${inputCls} flex-1`} data-testid={`${testidPrefix}-${k}-${i}`} />
          ))}
          <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-600 shrink-0" aria-label="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...items, Object.fromEntries(fields.map(([k]) => [k, ""]))])} className="border-zinc-300 text-zinc-600 rounded-sm text-xs uppercase font-bold" data-testid={`${testidPrefix}-add`}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add
      </Button>
    </div>
  );
};

export default function AdminPages() {
  const [pages, setPages] = useState(null);
  const [saving, setSaving] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [a, f, c] = await Promise.all([
        api.get("/admin/content/page_about"),
        api.get("/admin/content/page_financing"),
        api.get("/admin/content/page_contact"),
      ]);
      setPages({ page_about: a.data.data, page_financing: f.data.data, page_contact: c.data.data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!pages) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;
  }

  const setPage = (key, k, v) => setPages((p) => ({ ...p, [key]: { ...p[key], [k]: v } }));

  const save = async (key) => {
    setSaving(key);
    try {
      await api.put(`/admin/content/${key}`, { data: pages[key] });
      toast.success("Page saved. Changes are live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const SaveBtn = ({ pkey }) => (
    <Button onClick={() => save(pkey)} disabled={saving === pkey} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid={`${pkey}-save`}>
      {saving === pkey ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Page"}
    </Button>
  );

  return (
    <div data-testid="admin-pages">
      <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-6">Pages</h1>
      <Tabs defaultValue="about">
        <TabsList className="bg-white border border-zinc-200 rounded-sm mb-6">
          <TabsTrigger value="about" className="data-[state=active]:bg-[#FF6B00] data-[state=active]:text-black rounded-sm uppercase text-xs font-bold" data-testid="pages-tab-about">About</TabsTrigger>
          <TabsTrigger value="financing" className="data-[state=active]:bg-[#FF6B00] data-[state=active]:text-black rounded-sm uppercase text-xs font-bold" data-testid="pages-tab-financing">Financing</TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-[#FF6B00] data-[state=active]:text-black rounded-sm uppercase text-xs font-bold" data-testid="pages-tab-contact">Contact</TabsTrigger>
        </TabsList>

        <TabsContent value="about">
          <div className="bg-white border border-zinc-200 p-6 space-y-4">
            <F label="Hero Title" value={pages.page_about.hero_title} onChange={(v) => setPage("page_about", "hero_title", v)} testid="about-hero-title" />
            <F label="Hero Description" value={pages.page_about.hero_description} onChange={(v) => setPage("page_about", "hero_description", v)} testid="about-hero-desc" />
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Content Sections</Label>
              <SectionsEditor sections={pages.page_about.body_sections || []} onChange={(v) => setPage("page_about", "body_sections", v)} />
            </div>
            <SeoFields pkey="page_about" pages={pages} setPage={setPage} />
            <SaveBtn pkey="page_about" />
          </div>
        </TabsContent>

        <TabsContent value="financing">
          <div className="bg-white border border-zinc-200 p-6 space-y-4">
            <F label="Hero Title" value={pages.page_financing.hero_title} onChange={(v) => setPage("page_financing", "hero_title", v)} testid="financing-hero-title" />
            <F label="Hero Description" value={pages.page_financing.hero_description} onChange={(v) => setPage("page_financing", "hero_description", v)} testid="financing-hero-desc" />
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Benefit Cards</Label>
              <ListPairEditor
                items={pages.page_financing.benefits || []}
                onChange={(v) => setPage("page_financing", "benefits", v)}
                fields={[["title", "Title"], ["description", "Description"]]}
                testidPrefix="financing-benefit"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={pages.page_financing.show_calculator !== false} onChange={(e) => setPage("page_financing", "show_calculator", e.target.checked)} className="accent-[#FF6B00] h-4 w-4" data-testid="financing-show-calculator" />
              Show payment calculator
            </label>
            <SeoFields pkey="page_financing" pages={pages} setPage={setPage} />
            <SaveBtn pkey="page_financing" />
          </div>
        </TabsContent>

        <TabsContent value="contact">
          <div className="bg-white border border-zinc-200 p-6 space-y-4">
            <F label="Hero Title" value={pages.page_contact.hero_title} onChange={(v) => setPage("page_contact", "hero_title", v)} testid="contact-hero-title" />
            <F label="Hero Description" value={pages.page_contact.hero_description} onChange={(v) => setPage("page_contact", "hero_description", v)} testid="contact-hero-desc" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <F label="Address" value={pages.page_contact.address} onChange={(v) => setPage("page_contact", "address", v)} testid="contact-address" />
              <F label="Phone" value={pages.page_contact.phone} onChange={(v) => setPage("page_contact", "phone", v)} testid="contact-phone" />
              <F label="Email" value={pages.page_contact.email} onChange={(v) => setPage("page_contact", "email", v)} testid="contact-email" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Hours</Label>
              <ListPairEditor items={pages.page_contact.hours || []} onChange={(v) => setPage("page_contact", "hours", v)} fields={[["day", "Day(s)"], ["time", "Hours"]]} testidPrefix="contact-hours" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Departments</Label>
              <ListPairEditor items={pages.page_contact.departments || []} onChange={(v) => setPage("page_contact", "departments", v)} fields={[["name", "Department"], ["line", "Phone line"]]} testidPrefix="contact-dept" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={pages.page_contact.form_enabled !== false} onChange={(e) => setPage("page_contact", "form_enabled", e.target.checked)} className="accent-[#FF6B00] h-4 w-4" data-testid="contact-form-enabled" />
              Show contact form
            </label>
            <SeoFields pkey="page_contact" pages={pages} setPage={setPage} />
            <SaveBtn pkey="page_contact" />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
